/**
 * Frontend Sentry — error reporting for the React SPA.
 *
 * Mirrors the backend posture exactly:
 *   - No-op when REACT_APP_SENTRY_DSN is empty (the dev default).
 *     Safe to ship without a Sentry account configured.
 *   - PII scrubbing in beforeSend / beforeBreadcrumb so credentials
 *     never make it off the user's machine.
 *   - Scope tagging (user_id, org_id, role) wired from the auth store
 *     so frontend events carry the same tenant context as backend
 *     events captured under the same request_id.
 *
 * Source-map upload is intentionally NOT configured here — that's
 * deploy-pipeline work (Sentry CLI in CI). Without source maps,
 * stack traces show minified function names; Sentry still groups
 * events correctly, you just get less detail in the UI. Add the CLI
 * step when the deploy pipeline lands.
 */
import * as Sentry from "@sentry/react";

// ── Field names we treat as sensitive ──────────────────────────────
//
// Same list as backend/app/observability/sentry.py — keep in sync. If
// either side adds a sensitive field, mirror it here.

const SENSITIVE_KEY_FRAGMENTS = [
  "password",
  "secret",
  "token",
  "api_key",
  "authorization",
  "cookie",
];

const REDACTED_HEADERS = new Set([
  "authorization",
  "cookie",
  "x-twilio-signature",
  "x-csrftoken",
]);

const REDACTED = "[redacted]";

function isSensitiveKey(key) {
  if (typeof key !== "string") return false;
  const lower = key.toLowerCase();
  return SENSITIVE_KEY_FRAGMENTS.some((frag) => lower.includes(frag));
}

function scrubObject(obj) {
  // Walk a plain object and redact sensitive values in place. We
  // return a copy so the caller can mutate without surprising the SDK.
  if (!obj || typeof obj !== "object") return obj;
  const out = Array.isArray(obj) ? [] : {};
  for (const [key, value] of Object.entries(obj)) {
    if (isSensitiveKey(key)) {
      out[key] = REDACTED;
    } else if (value && typeof value === "object") {
      out[key] = scrubObject(value);
    } else {
      out[key] = value;
    }
  }
  return out;
}

function scrubHeaders(headers) {
  if (!headers || typeof headers !== "object") return headers;
  const out = {};
  for (const [key, value] of Object.entries(headers)) {
    if (REDACTED_HEADERS.has(key.toLowerCase())) {
      out[key] = REDACTED;
    } else {
      out[key] = value;
    }
  }
  return out;
}

// ── beforeSend / beforeBreadcrumb hooks ────────────────────────────

/**
 * Strip sensitive fields from event payloads before they leave the
 * browser. Runs on EVERY event Sentry tries to send. Errors here
 * would swallow events silently — keep this defensive.
 */
export function scrubEvent(event) {
  if (!event) return event;
  try {
    if (event.request) {
      if (event.request.headers) {
        event.request.headers = scrubHeaders(event.request.headers);
      }
      if (event.request.data && typeof event.request.data === "object") {
        event.request.data = scrubObject(event.request.data);
      }
    }
    if (event.extra) event.extra = scrubObject(event.extra);
    if (event.contexts) event.contexts = scrubObject(event.contexts);
  } catch {
    // Never let scrubbing failure prevent capture; the alternative
    // is dropping the event entirely. We err on the side of "send
    // a possibly-leaky event" because we'd rather know about a bug.
  }
  return event;
}

/**
 * fetch / xhr breadcrumbs include the request URL and (sometimes)
 * the request body. Redact Authorization headers and credential-
 * shaped bodies on the way to Sentry.
 */
export function scrubBreadcrumb(breadcrumb) {
  if (!breadcrumb) return breadcrumb;
  try {
    if (breadcrumb.data) {
      // Headers from xhr/fetch breadcrumbs.
      if (breadcrumb.data.headers) {
        breadcrumb.data.headers = scrubHeaders(breadcrumb.data.headers);
      }
      // Some SDK versions stash the request body on .body or .data.
      // Walk the entire data object so we catch both shapes.
      breadcrumb.data = scrubObject(breadcrumb.data);
    }
  } catch {
    // See scrubEvent.
  }
  return breadcrumb;
}

// ── Init ───────────────────────────────────────────────────────────

let _initialized = false;

/**
 * Initialize Sentry. Idempotent — calling again is a no-op.
 *
 * Reads config from REACT_APP_SENTRY_* env vars baked in at build
 * time by Create React App. When DSN is empty, returns false and
 * does nothing — same posture as the backend init.
 */
export function initFrontendSentry() {
  if (_initialized) return true;
  const dsn = process.env.REACT_APP_SENTRY_DSN || "";
  if (!dsn.trim()) return false;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "development",
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
    tracesSampleRate: parseFloat(
      process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || "0.1",
    ),
    // Critical: never opt into auto-PII collection. Mirrors the
    // backend's send_default_pii=False.
    sendDefaultPii: false,
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
    // The ErrorBoundary in App.jsx (added below) catches React tree
    // errors. The default integrations cover unhandled rejections,
    // global errors, and the fetch/xhr breadcrumb stream.
  });
  _initialized = true;
  return true;
}

// ── Scope tagging ──────────────────────────────────────────────────

/**
 * Attach the current user (and their org / role) to the Sentry
 * scope so subsequent events carry tenant context. Called from the
 * auth store on login + on session restore.
 *
 * No-op when Sentry isn't initialized (DSN empty) — Sentry's own
 * setUser / setTag are no-ops in that state, so we don't need a guard.
 */
export function setSentryUser(user) {
  if (!user) return;
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
  });
  if (user.org_id != null) {
    Sentry.setTag("org_id", String(user.org_id));
  }
  if (user.role) {
    Sentry.setTag("role", user.role);
  }
}

/**
 * Clear the user from the Sentry scope. Called from the auth store
 * on logout. Without this, subsequent error events (e.g. on the
 * login page after logout) would still be attributed to the prior
 * user.
 */
export function clearSentryUser() {
  Sentry.setUser(null);
  // Clearing tags is more invasive — Sentry doesn't expose a tag
  // delete. Setting them to null marks them as "not set" in the UI.
  Sentry.setTag("org_id", null);
  Sentry.setTag("role", null);
}

// Re-export Sentry for callers that want to capture custom events.
export { Sentry };
